import React, { useContext, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Context } from "../App";
import SiteFooter from "./SiteFooter";
import SiteThemePicker from "./SiteThemePicker";
import { useSiteTheme } from "../context/SiteThemeContext";

const SUPER_ADMIN = "SUPER_ADMIN";

/** Booking-only ADMIN sidebar (SUPER_ADMIN keeps top tabs). */
const STAFF_NAV_ITEMS = [
  { label: "PartyBoss", to: "/admin/dashboard" },
  { label: "Slot booking", to: "/admin/partyboss-slot-booking" },
  { label: "Payment failed", to: "/admin/payment-failed" },
  { label: "Banquet hall", to: "/admin/banquet-hall" },
  { label: "Customer leads", to: "/admin/customer-leads" },
];

/**
 * Shared chrome for logged-in admin routes. Renders nav tabs based on the admin's role:
 * SUPER_ADMIN sees every tab in the top bar; ADMIN sees a left sidebar (PartyBoss, Banquet, Leads).
 */
export default function AdminLayout({ title = "Console", children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { context, setContext } = useContext(Context);

  const role = String(context?.adminRole || "").toUpperCase();
  const isSuperAdmin = role === SUPER_ADMIN;
  const isStaffAdmin = Boolean(context?.isAdminLoggedIn) && !isSuperAdmin;

  const logout = () => {
    try {
      sessionStorage.removeItem("partyboss_admin_session");
    } catch {
      /* ignore */
    }
    setContext((prev) => ({
      ...(prev && typeof prev === "object" ? prev : {}),
      isAdminLoggedIn: false,
      adminRole: undefined,
      adminUserName: "",
    }));
    navigate("/admin");
  };

  const { themeId } = useSiteTheme();
  const isLounge = themeId === "premium";

  const onAdminArea =
    typeof location.pathname === "string" && location.pathname.startsWith("/admin");
  const isLoginRoute = location.pathname === "/admin";
  const showAdminToolbar = Boolean(context?.isAdminLoggedIn && onAdminArea && !isLoginRoute);

  const pathNorm = String(location.pathname || "").replace(/\/+$/, "") || "/admin";

  const navItems = useMemo(
    () => [
      { label: "Booking", to: "/admin/dashboard", requiresSuper: false },
      { label: "Slot booking", to: "/admin/partyboss-slot-booking", requiresSuper: false },
      { label: "Payment failed", to: "/admin/payment-failed", requiresSuper: false },
      { label: "Customer leads", to: "/admin/customer-leads", requiresSuper: false },
      { label: "Banquet hall", to: "/admin/banquet-hall", requiresSuper: false },
      { label: "Site content & media", to: "/admin/site-content", requiresSuper: true },
      { label: "Addons", to: "/admin/changedata", requiresSuper: true },
      { label: "Update theatre details", to: "/admin/theatre-gallery", requiresSuper: true },
      { label: "Promo codes", to: "/admin/promo-codes", requiresSuper: true },
      { label: "Marketing & QR", to: "/admin/marketing", requiresSuper: true },
      { label: "Booking analytics", to: "/admin/booking-analytics", requiresSuper: true },
    ],
    []
  );

  const pageClass = [
    "admin-console-page",
    "admin-console-page--with-footer",
    "pb-site-theme-scope",
    isLounge ? "pb-lounge-admin" : "pb-classic-admin",
    isStaffAdmin && showAdminToolbar ? "admin-console-page--staff-sidebar" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const staffSidebar = showAdminToolbar && isStaffAdmin ? (
    <aside className="analytics-subsection-sidebar admin-staff-sidebar" aria-label="Admin menu">
      <p className="analytics-subsection-sidebar-title">Menu</p>
      <nav className="analytics-subsection-nav">
        {STAFF_NAV_ITEMS.map((item) => {
          const active =
            pathNorm === item.to ||
            (item.to !== "/admin/dashboard" && pathNorm.startsWith(item.to));
          return (
            <button
              key={item.to}
              type="button"
              className={`analytics-subsection-nav-btn${active ? " analytics-subsection-nav-btn--active" : ""}`}
              onClick={() => navigate(item.to)}
              aria-current={active ? "page" : undefined}
            >
              <span className="analytics-subsection-nav-label">{item.label}</span>
            </button>
          );
        })}
      </nav>
      <div className="admin-staff-sidebar-footer">
        <button type="button" className="admin-console-nav-btn admin-console-nav-btn--primary w-100" onClick={logout}>
          Log out
        </button>
      </div>
    </aside>
  ) : null;

  return (
    <div className={pageClass} data-pb-site-theme={themeId}>
      {showAdminToolbar ? (
        <header
          className={`admin-console-header${isStaffAdmin ? " admin-console-header--staff" : ""}`}
        >
          <div className="admin-console-header-inner">
            {title ? <span className="admin-console-title">{title}</span> : null}
            {isSuperAdmin ? (
              <nav className="admin-console-nav" aria-label="Admin sections">
                {navItems
                  .filter((item) => isSuperAdmin || !item.requiresSuper)
                  .map((item) => {
                    const active =
                      pathNorm === item.to ||
                      (item.to !== "/admin/dashboard" && pathNorm.startsWith(item.to));
                    return (
                      <button
                        key={item.to}
                        type="button"
                        className={`admin-console-nav-btn${active ? " admin-console-nav-btn--active" : ""}`}
                        onClick={() => navigate(item.to)}
                      >
                        {item.label}
                      </button>
                    );
                  })}
                <SiteThemePicker compact label="Theme" className="ms-1 align-self-center" />
                <button
                  type="button"
                  className="admin-console-nav-btn admin-console-nav-btn--primary"
                  onClick={logout}
                >
                  Log out
                </button>
              </nav>
            ) : (
              <div className="admin-console-nav ms-auto">
                {context?.adminUserName ? (
                  <span className="text-secondary small me-2">{context.adminUserName}</span>
                ) : null}
              </div>
            )}
          </div>
        </header>
      ) : null}
      <div
        className={`admin-console-main${isStaffAdmin && showAdminToolbar ? " admin-console-main--staff" : ""}`}
        role="region"
        aria-label="Admin console content"
      >
        {staffSidebar ? (
          <div className="analytics-subsection-layout admin-staff-layout">
            {staffSidebar}
            <div className="analytics-subsection-main admin-console-content">{children}</div>
          </div>
        ) : (
          children
        )}
      </div>
      <div className="admin-console-site-footer-wrap">
        <SiteFooter />
      </div>
    </div>
  );
}
